import { Frame, useWidgetText } from "../shared";
import { dayMonth, localDate } from "../statistics";
import type { WidgetProps } from "../types";
import { StatisticsWidget } from "./StatisticsParts";

const PERIODS = ["today", "week", "month", "year"] as const;

/**
 * period_totals: four tiles, today, this week, this month and this year, each with its total so far; a period that
 * began before the first reading says since when (pro-statistics PST-12). On a narrow card the tiles wrap two by two.
 */
export function PeriodTotals(props: WidgetProps) {
  const fmt = useWidgetText();
  const { widget, now, tz } = props;
  return (
    <StatisticsWidget
      props={props}
      body={({ answer, point, name, icon, footer, offline }) => {
        const year = localDate(now.getTime(), tz).year;
        const tiles = PERIODS.map((period) => {
          const p = answer.periods?.find((x) => x.period === period);
          return {
            period,
            label: fmt.t(`statistics.${period}`),
            value: p && p.value !== null ? fmt.value(p.value, point) : "—",
            since: p?.since
              ? fmt.t("statistics.since", { date: dayMonth(localDate(Date.parse(p.since), tz), fmt.t, year) })
              : undefined,
          };
        });
        const label = `${name}: ${tiles
          .map((c) => [c.label.toLocaleLowerCase(fmt.locale), c.value, c.since].filter(Boolean).join(" "))
          .join(", ")}`;
        return (
          <Frame widget={widget} state={offline ? "offline" : "normal"} title={name} icon={icon} label={label}>
            <dl className="grid grid-cols-2 gap-2 @md:grid-cols-4">
              {tiles.map((c) => (
                <div key={c.period} className="min-w-0 rounded-lg bg-gray-50 px-2 py-1.5">
                  <dt className="text-xs leading-tight text-gray-700">{c.label}</dt>
                  <dd className="truncate text-xl font-semibold tracking-tight text-gray-900 tabular-nums">
                    {c.value}
                  </dd>
                  {c.since && <dd className="truncate text-xs text-gray-700">{c.since}</dd>}
                </div>
              ))}
            </dl>
            {footer}
          </Frame>
        );
      }}
    />
  );
}
